import { DYClient, TRIMMED_FIELDS, handleFromUrl } from '@theme/dy-core';

/**
 * @typedef {{ skeleton: HTMLLIElement, handle: string, source: 'dy' | 'native', slotId: string | null, decisionId: string | null }} FillItem
 */

/**
 * <dy-plp> — Dynamic Yield product listing (Option B).
 *
 * In DY mode the section server-renders one skeleton placeholder per product on each page
 * (snippets/dy-plp-skeleton.liquid). Every skeleton carries its Shopify page number (data-page)
 * and its own native product handle (data-native-handle). This element asks DY (Browse /
 * Category Pages) which products to show for that page, fetches the chosen cards through the
 * render seam (the ?view=card product page, one request per handle), and swaps them into the
 * placeholders. If DY errors or does not answer within the timeout budget, each placeholder is
 * filled with its own native product instead — so there is never a stuck or blank tile.
 *
 * DY owns the order on every page. On the DEFAULT (unfiltered/unsorted) paint Shopify normally owns
 * the page count and navigation too: the infinite-scroll <results-list> appends the next page's
 * skeletons into this element's grid (they mirror a card's ref="cards[]" / data-page hooks), and
 * a MutationObserver here fills those appended skeletons for their own DY page offset. This keeps
 * the SSR disruptor/teaser tiles that each native page renders.
 *
 * The exception is a collection DY covers only PART of. DY's feed is availability-filtered while
 * the Shopify collection's rules cannot be (there is no sellable-online rule column), so a
 * collection can hold far more products than DY will serve — Sale on mimco-au is 504 native vs 280
 * from DY. Native paging would then offer pages DY cannot fill, and every surplus slot would fall
 * back to its own native product, re-admitting the sold-out items DY had just excluded. So when DY's
 * total is smaller than the native page count, slots past that total are DROPPED rather than filled
 * natively, and — in paginated mode — DY takes the pager on the default paint too, sized to its own
 * total (see #fillPage for why the pager takeover is paginated-only).
 *
 * Filters and sort are DY-powered too (mirroring <dy-search>): the facet UI (dy-search-facets.js)
 * dispatches a `dy:search-request` event, which #applyRequest re-queries DY with the new
 * query.filters / query.sortBy and re-renders in place — no Shopify section morph. Once a filter
 * or sort is active, DY also owns pagination (its own IntersectionObserver) and native paging is
 * neutralised. The SSR page-1 teaser/disruptor tiles are kept through the re-query — <disruptor-grid>
 * re-slots them among the DY-ordered cards (and hides any past the loaded range) — so page 1 of a
 * filtered/sorted view still shows them. After each page-1 response this element emits
 * `dy:search-response` carrying DY's facets[] + total so the facet UI can render.
 * Native Shopify remains only as the JS-off / DY-timeout fallback and the feed-gap fallback (a
 * collection DY has no data for keeps its native cards via each skeleton's data-native-handle).
 *
 * Config is passed as data attributes (see connectedCallback). Product membership is scoped to
 * the collection by sending the collection title as both the DY category context and a
 * `categories` filter, so the listing always reflects the Shopify collection.
 *
 * @element dy-plp
 */
class DyPlp extends HTMLElement {
  /** Max render-seam fetches in flight at once — a burst of all handles trips rate limits (429). */
  static #FETCH_CONCURRENCY = 6;

  /** Cards within this index (0-based) are above the fold and keep loading="eager". */
  static #EAGER_COUNT = 8;

  /** Shared DOMParser instance — stateless, no need to allocate one per swap. */
  static #parser = new DOMParser();

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {MutationObserver | null} */
  #observer = null;

  /** @type {IntersectionObserver | null} DY-owned pagination sentinel (only once a filter/sort is active). */
  #intersectObserver = null;

  /** Skeletons already claimed by a fill pass, so a re-fired observer never double-fills them. */
  #claimed = new WeakSet();

  /** Outstanding page fills, for toggling the grid's aria-busy state. */
  #inFlight = 0;

  /** DY's total result count for the current query — set on page 1 when it can be trusted. */
  #dyTotal = null;

  /** Last DY page appended by DY-owned pagination (1 = the SSR page). */
  #dyPage = 1;

  /** @type {Record<string, string[]>} Active term-facet selections (field -> values). */
  #filters = {};

  /** @type {Record<string, [number, number]>} Active range selections (field -> [min, max]). */
  #ranges = {};

  /** @type {{ field: string, order: 'asc' | 'desc' } | null} Active sort, or null for the DY default. */
  #sort = null;

  /** @type {Element | null} The product grid this element fills. */
  #grid = null;

  /** Skeleton media aspect ratio, captured from the SSR skeletons so JS-built skeletons match. */
  #skeletonRatio = '4 / 5';

  /**
   * True once a DY filter/sort interaction has neutralised native infinite scroll. Latched for the
   * session so DY keeps owning pagination even after the shopper clears back to no filters (native
   * paging can't be safely resumed once its bound was capped).
   */
  #nativePagingDisabled = false;

  /**
   * True once the first DY response of the current page load has owned the facet sidebar, product
   * count and no-results state. On the default infinite-scroll paint that first response is page 1;
   * in paginated mode a fresh navigation to page N makes page N the first response of that load, so
   * this latch (rather than `page === 1`) decides which fill re-renders those page-level pieces.
   */
  #didInitialResponse = false;

  /**
   * The page whose fill owns the page-level pieces this load (facets/count/no-results). On the SSR
   * paint it is the current Shopify page (1 for infinite scroll, N for a paginated ?page=N load); a
   * filter/sort re-query resets it to 1. Pinning the latch to this page stops a fast infinite-scroll
   * append (page 2) from claiming the facet render out from under page 1.
   */
  #responsePage = 1;

  /**
   * Merchant pagination style, mirrored from the section's `enable_infinite_scroll` setting. When
   * false the collection is in paginated (numbered) mode, so once DY owns pagination it renders
   * numbered page controls instead of the infinite-scroll IntersectionObserver.
   */
  #infiniteScroll = true;

  /** @type {HTMLElement | null} The DY-driven numbered pagination nav, when in paginated mode. */
  #pagerNav = null;

  /**
   * True while the pending #applyRequest is the initial URL-selection restore (the shopper reloaded
   * a `?dyf_*`/`dysort` + `?page=N` URL) rather than a live filter/sort change. On restore the DY
   * query must honour the refreshed page (#responsePage) instead of snapping to page 1; a later
   * filter/sort change is a fresh query and does reset to page 1. Consumed by the first #applyRequest.
   */
  #restorePending = false;

  /** Bound `dy:search-request` handler, kept for add/removeEventListener symmetry. */
  #onRequest = (/** @type {Event} */ event) => {
    if (event instanceof CustomEvent) this.#applyRequest(event.detail);
  };

  /** @type {import('@theme/dy-core').DYClient | null} Cached client — initialised in connectedCallback, reused for #raceDy and slot click reporting. */
  #client = null;

  /** Sets up the abort controller, status node, and MutationObserver; fills initial skeletons. */
  connectedCallback() {
    // Re-create the controller on every (re)connect so a reconnected element isn't left with a
    // controller already aborted by a previous disconnect.
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.#claimed = new WeakSet();
    this.#inFlight = 0;
    this.#dyTotal = null;
    this.#dyPage = 1;
    this.#intersectObserver?.disconnect();
    this.#intersectObserver = null;
    this.#filters = {};
    this.#ranges = {};
    this.#sort = null;
    this.#nativePagingDisabled = false;
    this.#didInitialResponse = false;
    this.#pagerNav = null;
    this.#infiniteScroll = this.dataset.infiniteScroll !== 'false';
    this.#client = this.dataset.apiKey ? new DYClient({
      apiKey: this.dataset.apiKey,
      dataCenter: this.dataset.dataCenter === 'eu' ? 'eu' : 'com',
      storeUrl: this.dataset.storeUrl,
      locale: this.dataset.locale || '',
      inStockOnly: this.dataset.inStockOnly === 'true',
      // SSR-stamped `product_live_{market}` feed column, so DY hard-filters this market's
      // catalogue on every serve. Empty for non-au/nz markets → no availability filter.
      availabilityField: this.dataset.availabilityField || '',
    }) : null;

    const grid = this.querySelector('ul.product-grid');
    // No grid or no SSR skeletons in it means the visible page is empty — either an empty collection
    // or an out-of-range paginated page (?page=N past the last page). The SSR count still reflects
    // collection.products_count, which misleads shoppers looking at an empty grid. Override to 0 so
    // the label matches what's on screen; DY isn't queried in this state, so nothing else updates it.
    const firstSkeleton = grid?.querySelector('[data-dy-skeleton]') ?? null;
    if (!firstSkeleton) this.#updateCount(0);
    if (!grid) return;
    this.#grid = grid;

    // The SSR page this load is centred on: 1 for infinite scroll, N for a paginated ?page=N load.
    // The first fill of this page owns the facet sidebar / count (see #responsePage).
    this.#responsePage = Number(firstSkeleton?.getAttribute('data-page')) || 1;
    // Seed the DY page tracker from the SSR page so a direct ?page=N load is correct: in paginated
    // mode the numbered pager marks page N (not 1) as current, and in infinite-scroll mode the next
    // appended batch is N+1 (not a duplicate page 2). Reset to 1 by #applyRequest on filter/sort.
    this.#dyPage = this.#responsePage;
    // When this load restores a URL selection (dyf_/dysort), the follow-up #applyRequest must keep
    // the refreshed page rather than snap to page 1 (see #restorePending).
    this.#restorePending = this.#hasUrlSelection();

    const ratio = grid
      .querySelector('[style*="--dy-skeleton-media-ratio"]')
      ?.style.getPropertyValue('--dy-skeleton-media-ratio')
      .trim();
    if (ratio) this.#skeletonRatio = ratio;

    document.addEventListener('dy:search-request', this.#onRequest);

    if (!document.querySelector('[data-dy-status]')) {
      const status = document.createElement('span');
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      status.setAttribute('aria-atomic', 'true');
      status.dataset.dyStatus = '';
      status.className = 'dy-status';
      // Appended to <body> rather than this element: <dy-plp> has display:contents which removes
      // it from the formatting context, so a position:absolute child inside it climbs to an
      // unpredictable containing block. A body-level node is always off-screen.
      document.body.appendChild(status);
    }

    // When the URL carries an active DY selection, dy-search-facets restores it and dispatches a
    // `dy:search-request` on load, and #applyRequest paints the filtered view. Skip our own initial
    // unfiltered fill in that case so we don't fire a DY browse for the default order that would be
    // immediately aborted and discarded. #hasUrlSelection mirrors dy-search-facets' restore
    // condition exactly, so we only skip when a filtered request is guaranteed to follow. Deferred a
    // frame so a same-tick #applyRequest can abort/replace before the default fill would start.
    if (!this.#hasUrlSelection()) {
      requestAnimationFrame(() => this.#collectAndFill(grid));
    }

    grid.addEventListener('click', this.#handleSlotClick);

    // Watch for skeletons appended later by infinite scroll (<results-list> appends the next
    // page's items into this grid). Only childList on the grid itself: our own swaps mutate each
    // <li>'s children, not the grid's child list, so they don't re-trigger this.
    this.#observer = new MutationObserver((mutations) => {
      const hasNewSkeleton = mutations.some((m) =>
        [...m.addedNodes].some(
          (node) =>
            node instanceof Element &&
            (node.matches('[data-dy-skeleton]') || node.querySelector('[data-dy-skeleton]'))
        )
      );
      if (hasNewSkeleton) this.#collectAndFill(grid);
    });
    this.#observer.observe(grid, { childList: true });
  }

  /** Aborts outstanding fetches, disconnects the MutationObserver, and removes the status node. */
  disconnectedCallback() {
    this.#abortController?.abort();
    this.#observer?.disconnect();
    this.#observer = null;
    this.#intersectObserver?.disconnect();
    this.#intersectObserver = null;
    if (this.#pagerNav) {
      this.#pagerNav.removeEventListener('click', this.#handlePageClick);
      // Clear the DOM-side bind marker too: if this element reconnects while the nav node survives,
      // #renderDyPagination's `!nav.dataset.dyBound` guard must re-attach the listener.
      delete this.#pagerNav.dataset.dyBound;
    }
    this.#pagerNav = null;
    this.querySelector('ul.product-grid')?.removeEventListener('click', this.#handleSlotClick);
    this.#client = null;
    document.removeEventListener('dy:search-request', this.#onRequest);
    document.querySelector('[data-dy-status]')?.remove();
  }

  /**
   * Claim every unfilled skeleton currently in the grid, group them by their Shopify page, and
   * fill each page group from DY (or its per-slot native fallback). Runs on first paint and again
   * for each infinite-scroll append.
   *
   * @param {Element} grid
   */
  #collectAndFill(grid) {
    const fresh = /** @type {HTMLLIElement[]} */ (
      [...grid.querySelectorAll('[data-dy-skeleton]')].filter((sk) => !this.#claimed.has(sk))
    );
    if (fresh.length === 0) return;

    /** @type {Map<number, HTMLLIElement[]>} */
    const byPage = new Map();
    for (const skeleton of fresh) {
      this.#claimed.add(skeleton);
      const page = Number(skeleton.dataset.page) || 1;
      const group = byPage.get(page);
      if (group) group.push(skeleton);
      else byPage.set(page, [skeleton]);
    }

    for (const [page, skeletons] of byPage) {
      void (async () => {
        try {
          await this.#fillPage(grid, page, skeletons);
        } catch (error) {
          if (this.#abortController?.signal.aborted) return;
          console.warn(`[dy-plp] page ${page} fill failed:`, error);
        }
      })();
    }
  }

  /**
   * Fill one page's skeletons: race DY (at that page's offset) against the timeout budget, then
   * swap in DY's cards where it answered and each skeleton's own native product where it did not.
   *
   * @param {Element} grid
   * @param {number} page - 1-based Shopify page (DY offset page = page - 1).
   * @param {HTMLLIElement[]} skeletons - The page's placeholder <li>s, in DOM order.
   * @returns {Promise<void>}
   */
  async #fillPage(grid, page, skeletons) {
    const signal = this.#abortController?.signal;
    const timeoutMs = Number(this.dataset.timeout) || 2000;
    const pageSize = Number(this.dataset.pageSize) || skeletons.length;

    this.#setBusy(grid, 1);
    try {
      let dyResult = null;
      if (this.dataset.apiKey) {
        try {
          dyResult = await this.#raceDy(page - 1, pageSize, timeoutMs);
        } catch {
          dyResult = null; // fall through to the per-slot native fallback
        }
      }
      if (signal?.aborted) return;

      const active = this.#isActive();
      // The first DY response of this page load owns the page-level pieces (facet sidebar, product
      // count, no-results). On the default infinite-scroll paint that is page 1; in paginated mode a
      // fresh navigation to page N makes page N the first response, so gate on the latch rather than
      // `page === 1` — otherwise the client-side facets never render on page 2+.
      const isFirstResponse = !this.#didInitialResponse && page === this.#responsePage;
      if (isFirstResponse) {
        this.#didInitialResponse = true;
        // On the DEFAULT paint a DY total of 0/null means "DY has no data for this collection"
        // (a feed gap): the grid falls back to native cards below, so keep the native count and
        // never show the no-results state. Only trust DY's total once a filter/sort is active or
        // DY actually returned results.
        const total = dyResult?.totalNumResults ?? null;
        const trustTotal = active || (total ?? 0) > 0;
        this.#dyTotal = trustTotal ? total : null;
        // Always settle the count, even when DY's total can't be trusted: on the fallback path the
        // grid fills with native cards, so the native label it was rendered with is the right one
        // and simply needs revealing. Passing null leaves its text alone and just reveals it.
        this.#updateCount(trustTotal ? total : null);
        // Hand DY's facets + active selection to the facet UI so it can (re)render the sidebar.
        this.#emitResponse(dyResult);

        // Hand DY pagination on the DEFAULT paint too — not only after a filter/sort — whenever DY's
        // result set is SMALLER than the collection's native page count. Native paging is sized by
        // Shopify collection membership, which includes products DY deliberately leaves out (sold-out
        // ones, most visibly: DY's feed is availability-filtered, the collection's rules can't be —
        // Shopify has no sellable-online rule column). Every page past DY's last would then be filled
        // slot-by-slot from `data-native-handle` and re-admit exactly what DY excluded. Latch only on
        // the mismatch: when DY covers every native page there is nothing to fix, and leaving native
        // paging in charge keeps the per-page SSR teaser/disruptor tiles.
        //
        // Paginated mode only. In infinite-scroll mode <results-list> can have the next native page
        // in flight before DY answers (its pre-fetch starts well inside the DY timeout budget), so
        // taking the pager over mid-load races its append against #setupDyPagination's and can
        // double-append a page. Those brands still get the correct GRID from the dyReach drop below
        // — slots past DY's total are removed rather than back-filled natively — they just keep
        // scrolling through the now-empty tail of the native page count.
        if (trustTotal && !active && !this.#infiniteScroll && !this.#nativePagingDisabled) {
          const nativeLastPage = Number(grid.getAttribute('data-last-page')) || 0;
          const dyLastPage = Math.ceil((total ?? 0) / pageSize) || 1;
          if (nativeLastPage > dyLastPage) {
            this.#nativePagingDisabled = true;
            // Cap the native bound so <results-list> stops fetching further native pages; DY's own
            // pager (rendered at the end of this fill) takes over from here.
            grid.setAttribute('data-last-page', String(Math.min(page, dyLastPage)));
            // A direct load of ?page=N past DY's last page (an old link, or a crawl of the larger
            // native page count) has no DY products to show and would strand the shopper on an empty
            // grid — repaint DY's last page instead. #settlePagination first, so the pending shimmer
            // is retired before #goToDyPage renders DY's own pager over it.
            if (page > dyLastPage) {
              this.#settlePagination(grid);
              this.#goToDyPage(dyLastPage);
              return;
            }
          }
        }

        this.#settlePagination(grid);
      }

      // DY handle where it answered for this slot, otherwise the slot's own native product.
      // Native fallback is only safe when no filter/sort is active — its cards are the collection's
      // unfiltered order, which must never leak into a DY-filtered/sorted view.
      const dyItems = dyResult?.items ?? null;
      const decisionId = dyResult?.decisionId ?? null;
      const allowNative = !active;
      // How far into this page DY's result set actually reaches. Slots past it sit beyond DY's total
      // (the tail of its last page, or a page beyond it): DY has no product for them, and the native
      // product at that position is precisely what DY's availability filter excluded — so they must
      // NOT fall back. An empty handle makes #swapAll drop the placeholder instead. Null while DY's
      // total is unknown (feed gap, or a timeout before page 1 answered), where the native fallback
      // below is still the right answer for every slot.
      const dyReach = this.#dyTotal === null
        ? null
        : Math.max(0, Math.min(skeletons.length, this.#dyTotal - (page - 1) * pageSize));
      const fills = skeletons.map((skeleton, index) => {
        const dyItem = dyItems?.[index];
        if (dyItem?.handle) return { skeleton, handle: dyItem.handle, source: 'dy', slotId: dyItem.slotId, decisionId };
        const beyondDy = dyReach !== null && index >= dyReach;
        const nativeHandle = allowNative && !beyondDy ? (skeleton.dataset.nativeHandle ?? '') : '';
        return { skeleton, handle: nativeHandle, source: 'native', slotId: null, decisionId: null };
      });

      // Absolute position in the full grid (0-based across all pages) so #swap correctly
      // marks only above-the-fold images eager — page 2+ is always off-screen at fill time.
      const indexOffset = (page - 1) * pageSize;
      await this.#swapAll(fills, signal, indexOffset);
      if (signal?.aborted) return;

      if (isFirstResponse && active) this.#toggleNoResults(dyResult?.totalNumResults ?? null);

      // Let a wrapping <disruptor-grid> slot its teaser tiles among the now-present cards (the
      // swaps mutate <li>s in place, which its grid observer doesn't catch). Bubbles to the host.
      this.dispatchEvent(new CustomEvent('dy-plp:filled', { bubbles: true, composed: false }));

      // DY owns pagination once a filter/sort has been used, and on the default paint too when its
      // set is smaller than the collection's native page count (both latch #nativePagingDisabled).
      // A default paint DY can cover keeps native <results-list> paging, so its SSR teaser tiles
      // survive across pages. Honour the merchant's pagination style: infinite scroll uses the
      // load-more IntersectionObserver; paginated mode renders numbered DY page controls.
      if (active || this.#nativePagingDisabled) {
        if (this.#infiniteScroll) this.#setupDyPagination(grid, pageSize);
        else this.#renderDyPagination(grid, pageSize);
      }
    } finally {
      // Defer by one frame so aria-busy clears after the browser paints the swapped-in cards,
      // giving AT a chance to perceive the busy state for the full update window.
      requestAnimationFrame(() => this.#setBusy(grid, -1));
    }
  }

  /**
   * Build the render-seam URL for a card. Routes through the collection-scoped product URL
   * (`/collections/{handle}/products/{handle}?view=card`) so the card render has the current
   * collection in context: the overlay-badge resolver then scopes to this collection instead of
   * falling back to all of the product's collections, which would leak other collections' badges
   * onto this grid. Returns the plain product URL when no collection handle is present, or when
   * `useCollection` is false — the collection-independent last resort {@link #fetchFallback} uses
   * so a stale/unpublished collection route can't blank the whole grid.
   *
   * @param {string} root - Shopify routes root prefix (e.g. '/' or '/en-au/').
   * @param {string} handle - Shopify product handle.
   * @param {boolean} [useCollection=true] - Scope to the current collection when a handle is set.
   * @returns {string}
   */
  #cardUrl(root, handle, useCollection = true) {
    const collection = useCollection ? this.dataset.collectionHandle : '';
    return collection
      ? `${root}collections/${collection}/products/${handle}?view=card`
      : `${root}products/${handle}?view=card`;
  }

  /**
   * Fetch each fill's card through the render seam and swap it into its skeleton. Runs through a
   * bounded pool ({@link #FETCH_CONCURRENCY}) so a page of handles does not fire dozens of
   * simultaneous requests (which the storefront rate-limits with 429s).
   *
   * @param {FillItem[]} fills
   * @param {AbortSignal | undefined} signal
   * @param {number} [indexOffset=0] - Absolute grid index of the first fill (0 for page 1,
   *   pageSize for page 2, etc.). Passed through to {@link #swap} so lazy/eager loading is
   *   based on absolute position rather than per-page position.
   * @returns {Promise<void>}
   */
  async #swapAll(fills, signal, indexOffset = 0) {
    const root = window.Shopify?.routes?.root ?? '/';

    let next = 0;
    const worker = async () => {
      while (next < fills.length) {
        const index = next;
        const { skeleton, handle, source, slotId, decisionId } = fills[index];
        next += 1;
        if (signal?.aborted) return;
        if (!handle) {
          // No DY result and no allowed native fallback (a filter/sort is active) — remove the
          // placeholder so it doesn't linger as a loading skeleton.
          skeleton.remove();
          continue;
        }
        try {
          // Render seam = the ?view=card product page (templates/product.card.json). We fetch the
          // whole card-view page and lift out the <product-card>. The Section Rendering API
          // (?sections=) can't hydrate the card's nested static blocks, so it is NOT used here.
          const response = await fetch(this.#cardUrl(root, handle), { signal });
          if (!response.ok) throw new Error(`card ${response.status}`);
          this.#swap(skeleton, await response.text(), handle, source, indexOffset + index, slotId, decisionId);
        } catch (error) {
          if (signal?.aborted) return;
          // On a DY-directed card failure, fall back to the slot's own native product so the
          // tile never stays as a shimmering placeholder.
          await this.#fetchFallback(skeleton, handle, signal, indexOffset + index, root, error);
        }
      }
    };

    const poolSize = Math.min(DyPlp.#FETCH_CONCURRENCY, fills.length);
    await Promise.all(Array.from({ length: poolSize }, worker));
  }

  /**
   * On a primary card-fetch failure, retry the slot's own native product through the render seam,
   * degrading the collection-scoped URL to the plain product URL so a broken/renamed collection
   * route degrades to unscoped cards rather than a blank grid. Applies to both DY- and
   * native-sourced fills. If there is no native handle, or every retry fails, the skeleton is
   * removed so it neither shimmers forever nor stays hidden from AT.
   *
   * @param {HTMLLIElement} skeleton
   * @param {string} handle - The handle whose primary fetch failed.
   * @param {AbortSignal | undefined} signal
   * @param {number} index
   * @param {string} root - Shopify routes root prefix.
   * @param {unknown} error - The original fetch error (for logging).
   * @returns {Promise<void>}
   */
  async #fetchFallback(skeleton, handle, signal, index, root, error) {
    const nativeHandle = skeleton.dataset.nativeHandle ?? '';
    if (!nativeHandle) {
      console.warn(`[dy-plp] card fetch failed for ${handle}:`, error);
      skeleton.remove();
      return;
    }

    // Retry the slot's own native product, degrading collection-scoped -> unscoped so a broken or
    // renamed collection route can't blank the grid (the plain product URL is always valid). When
    // the failed handle IS the native handle, the scoped native card was already the failed primary,
    // so skip straight to the unscoped retry — during a collection-route outage every tile falls
    // here and re-issuing the identical scoped request would just double the load (429s). Only a
    // distinct DY handle leaves the scoped native card still worth a first attempt.
    const scopedAlreadyTried = handle === nativeHandle;
    const attempts = !scopedAlreadyTried && this.dataset.collectionHandle ? [true, false] : [false];
    for (const useCollection of attempts) {
      if (signal?.aborted) return;
      try {
        const fallback = await fetch(this.#cardUrl(root, nativeHandle, useCollection), { signal });
        if (!fallback.ok) throw new Error(`card ${fallback.status}`);
        this.#swap(skeleton, await fallback.text(), nativeHandle, 'native', index);
        return;
      } catch {
        if (signal?.aborted) return;
      }
    }
    if (scopedAlreadyTried) {
      console.warn(`[dy-plp] card fetch failed for ${handle}; no fallback product available`);
    } else {
      console.warn(`[dy-plp] card fetch failed for ${handle}; native fallback (${nativeHandle}) also failed`);
    }
    skeleton.remove();
  }

  /**
   * Resolve the DY handle list for a page within the timeout budget, or `null` if the budget
   * elapses first (a late response is ignored — its skeletons are already claimed/filled).
   *
   * @param {number} dyPage - 0-based DY page; offset = dyPage * pageSize.
   * @param {number} pageSize
   * @param {number} timeoutMs
   * @returns {Promise<{ items: Array<{handle: string, slotId: string | null}>, decisionId: string | null, totalNumResults: number | null, facets: unknown[] | null } | null>}
   */
  async #raceDy(dyPage, pageSize, timeoutMs) {
    if (!this.#client) return null;

    const category = this.dataset.category || '';
    /** @type {Record<string, string[]>} */
    const filters = {};
    // Scope DY to the Shopify collection: `categories` (the feed's collection-title column) must
    // match the collection or DY would return its whole catalogue. Merge in the shopper's DY facet
    // selections, but never their own `categories` values — the collection scope stays fixed so a
    // sub-category facet can't OR-broaden the listing beyond this collection.
    if (category) filters.categories = [category];
    for (const [field, values] of Object.entries(this.#filters)) {
      if (field === 'categories') continue;
      if (values.length > 0) filters[field] = values;
    }
    // in_stock is applied via the DYClient's inStockOnly config (buildFilters adds it), matching
    // dy-search.js — no manual push here.

    // The page the customer is actually on — real viewing domain plus the live path, query, page
    // and facet/sort params. Built from window.location (not data-store-url) so DY's
    // context.page.location tracks pagination and filters instead of freezing at the page-1 URL.
    const location = `${window.location.origin}${window.location.pathname}${window.location.search}`;

    /** @type {Promise<null>} */
    const timeoutPromise = new Promise((resolve) => {
      const signal = this.#abortController?.signal;
      const onAbort = () => { clearTimeout(id); resolve(null); };
      let id = setTimeout(() => {
        signal?.removeEventListener('abort', onAbort);
        resolve(null);
      }, timeoutMs);
      signal?.addEventListener('abort', onAbort, { once: true });
    });

    const result = await Promise.race([
      this.#client.browse(category, {
        pageSize,
        page: dyPage,
        filters,
        ranges: this.#ranges,
        sort: this.#sort ?? undefined,
        location,
        fields: TRIMMED_FIELDS, // url + group_id only — we render via the seam, not from productData
      }),
      timeoutPromise,
    ]);

    if (result === null) return null;
    const slots = result?.slots ?? [];
    const items = slots
      .map((slot) => ({ handle: handleFromUrl(slot?.productData?.url ?? '') ?? '', slotId: slot?.slotId ?? null }))
      .filter((item) => item.handle.length > 0);
    return {
      items,
      decisionId: result?.decisionId ?? null,
      totalNumResults: result?.totalNumResults ?? null,
      facets: Array.isArray(result?.facets) ? result.facets : null,
    };
  }

  /**
   * True when the URL carries an active DY selection (a `dyf_*` term filter, a `dyfr_*` range, or a
   * non-relevance `dysort`). Mirrors dy-search-facets' #readUrl + restore condition: when it returns
   * true, dy-search-facets dispatches a URL-restored `dy:search-request` on load, so this element
   * skips its own initial unfiltered fill and lets #applyRequest paint the filtered view instead.
   *
   * @returns {boolean}
   */
  #hasUrlSelection() {
    const params = new URLSearchParams(window.location.search);
    for (const [key, raw] of params) {
      if (key === 'dysort') {
        if (raw && raw !== 'relevance') return true;
      } else if (key.startsWith('dyf_')) {
        if (raw.split(',').some((value) => value.trim())) return true;
      } else if (key.startsWith('dyfr_')) {
        const [min, max] = raw.split(':').map(Number);
        if (Number.isFinite(min) && Number.isFinite(max)) return true;
      }
    }
    return false;
  }

  /**
   * True when any facet/range filter or a non-default sort is active.
   *
   * @returns {boolean}
   */
  #isActive() {
    return (
      this.#sort !== null ||
      Object.values(this.#filters).some((values) => values.length > 0) ||
      Object.keys(this.#ranges).length > 0
    );
  }

  /**
   * Re-query DY with a new filter/sort selection and re-render the grid in place — no Shopify
   * section morph. Neutralises native infinite scroll (DY owns pagination from here), keeps the SSR
   * page-1 teaser tiles (re-slotted by <disruptor-grid> among the new order), resets pagination to
   * page 1, and rebuilds a fresh page of blank skeletons; the next #fillPage(page 1) picks up the new
   * #filters/#ranges/#sort.
   *
   * @param {{ filters?: Record<string, string[]>, ranges?: Record<string, [number, number]>, sort?: { field: string, order: 'asc' | 'desc' } | null } | null} detail
   * @returns {void}
   */
  #applyRequest(detail) {
    const grid = this.#grid;
    if (!grid) return;

    this.#filters = detail?.filters ?? {};
    this.#ranges = detail?.ranges ?? {};
    this.#sort = detail?.sort ?? null;
    const active = this.#isActive();

    // Cancel in-flight fills and reset pagination state for the new query.
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.#claimed = new WeakSet();
    this.#inFlight = 0;
    this.#dyTotal = null;
    // On the initial URL-selection restore, honour the page the shopper refreshed on (the SSR page,
    // mirrored from ?page=N into #responsePage) instead of snapping to page 1. Any later filter/sort
    // *change* is a fresh query and resets to page 1.
    const isInitialRestore = this.#restorePending;
    const page = this.#restorePending ? this.#responsePage : 1;
    this.#restorePending = false;
    this.#dyPage = page;
    // The new query's first fill re-owns the facet sidebar, count and no-results.
    this.#didInitialResponse = false;
    this.#responsePage = page;
    this.#intersectObserver?.disconnect();
    this.#intersectObserver = null;

    const pageSize = Number(this.dataset.pageSize) || 24;
    if (active) {
      // Neutralise native <results-list> infinite scroll: cap the grid's reported last page so
      // paginated-list stops fetching further native pages (its bound is the unfiltered collection's
      // page count). DY owns pagination from here on, latched for the session (#nativePagingDisabled)
      // so clearing filters can't resume the now-capped native paging.
      this.#nativePagingDisabled = true;
      grid.setAttribute('data-last-page', '1');
    }
    // Rebuild the skeletons for the incoming query, EXCEPT on the one case where the grid still holds
    // its untouched SSR skeletons: the initial URL-restore that normalised to an empty selection (e.g.
    // a hidden/invalid ?dysort reset to relevance, still dispatched to unstick the skipped initial
    // fill). There we fill the existing skeletons in place and keep the default view's teaser tiles +
    // native paging. Every other inactive path — a live "Clear all" or unchecking the last facet from
    // an already-rendered grid — must rebuild, since the rendered cards are real products, not
    // skeletons, so #collectAndFill would otherwise find nothing to fill and freeze the stale view.
    if (active || !isInitialRestore) {
      this.#resetSkeletons(grid, pageSize, page);
    }
    this.#collectAndFill(grid);
  }

  /**
   * Replace every rendered product card/skeleton with a fresh page of blank skeletons (no native
   * handle — a DY re-query has no native equivalent). The SSR disruptor/teaser tiles are PRESERVED
   * so a filtered/sorted view keeps them: they slot by absolute grid position
   * (data-disruptor-position) via <disruptor-grid>, which re-places them on the dy-plp:filled event
   * and hides any whose slot falls outside the loaded page range. Only page-1-range tiles are SSR'd,
   * so they surface on page 1 (data-disruptor-offset 0) and drop out of range on later numbered
   * pages. Also preserves the hidden CSS-priming card.
   *
   * @param {Element} grid
   * @param {number} count
   * @param {number} [page=1] - Shopify page to stamp on each skeleton (data-page), so #fillPage
   *   queries DY at the matching offset. Used by numbered DY pagination to repaint page N.
   * @returns {void}
   */
  #resetSkeletons(grid, count, page = 1) {
    const primingCard = grid.querySelector('[data-css-prime]');
    for (const item of grid.querySelectorAll('.product-grid__item')) {
      if (item === primingCard) continue;
      if (item.classList.contains('collection-grid__disruptor-tile')) {
        // Keep only page-1-range tiles (position <= a page's worth of products). Later-page tiles
        // that native infinite scroll appended before the shopper filtered/sorted are dropped, so a
        // DY-owned view shows the page-1 teasers only — deterministically, whatever was scrolled first.
        const position = parseInt(item.getAttribute('data-disruptor-position') ?? '0', 10) || 0;
        if (position > 0 && position <= count) continue;
      }
      item.remove();
    }
    // Keep the disruptor slot origin aligned with the loaded page: page 1 -> 0 so its tiles show; a
    // later numbered page -> that page's product offset, so the page-1 tiles fall out of range and
    // <disruptor-grid> hides them. Infinite scroll only ever resets page 1 (page 2+ is appended), so
    // this stays 0 there and tiles slot at their absolute position across the growing list.
    grid.setAttribute('data-disruptor-offset', String((page - 1) * count));
    const fragment = document.createDocumentFragment();
    for (let i = 0; i < count; i++) fragment.appendChild(this.#buildSkeletonLi(page));
    if (primingCard) grid.insertBefore(fragment, primingCard);
    else grid.appendChild(fragment);
  }

  /**
   * Build one blank skeleton <li> for the given page. Marked `[data-dy-skeleton]` so the
   * MutationObserver / #collectAndFill picks it up and fills it from DY.
   *
   * @param {number} page
   * @returns {HTMLLIElement}
   */
  #buildSkeletonLi(page) {
    const li = document.createElement('li');
    li.className = 'product-grid__item dy-skeleton';
    li.setAttribute('data-dy-skeleton', '');
    li.setAttribute('ref', 'cards[]');
    li.setAttribute('data-page', String(page));
    li.setAttribute('aria-hidden', 'true');
    li.style.setProperty('--dy-skeleton-media-ratio', this.#skeletonRatio);
    li.innerHTML =
      '<div class="dy-skeleton__card">' +
        '<div class="dy-skeleton__media"></div>' +
        '<div class="dy-skeleton__line dy-skeleton__line--title"></div>' +
        '<div class="dy-skeleton__line dy-skeleton__line--price"></div>' +
      '</div>';
    return li;
  }

  /**
   * Emit `dy:search-response` with DY's facets + total and the active selection, so the facet UI
   * (dy-search-facets.js) can (re)render the sidebar and sort control.
   *
   * @param {{ totalNumResults: number | null, facets: unknown[] | null } | null} dyResult
   * @returns {void}
   */
  #emitResponse(dyResult) {
    document.dispatchEvent(
      new CustomEvent('dy:search-response', {
        detail: {
          facets: dyResult?.facets ?? null,
          total: dyResult?.totalNumResults ?? null,
          filters: this.#filters,
          ranges: this.#ranges,
          sort: this.#sort,
        },
      })
    );
  }

  /**
   * Settle the product count: replace its text with DY's total when that total can be trusted, drop
   * the pending shimmer, and reveal the label either way. Called on the first DY response whatever
   * the outcome — on the fallback path (DY timed out, grid filled with native cards) the native
   * label is the correct one, so it is revealed unchanged rather than left shimmering forever.
   * The locale strings use `__DY_COUNT__` as a placeholder rendered server-side via
   * `t: count: '__DY_COUNT__'` so JS can substitute without reimplementing the t: filter.
   *
   * @param {number | null} total
   * @returns {void}
   */
  #updateCount(total) {
    const wrappers = document.querySelectorAll('[data-testid="products-count"], [data-testid="products-count-mobile"]');
    if (!wrappers.length) return;

    if (total !== null && this.dataset.labelCount) {
      const text = total > 25000
        ? (this.dataset.labelCountCutoff ?? '')
        : this.dataset.labelCount.replace('__DY_COUNT__', String(total));
      for (const wrapper of wrappers) {
        // Prefer the dedicated value span when the shared count markup uses the term/value split
        // (search), then the PLP's labelled count span. The bare `span` fallback is last and must
        // stay last: in the pending state the shimmer placeholder is the wrapper's FIRST span, and
        // writing the count into that would leave it invisible behind the skeleton styling.
        const span = wrapper.querySelector('[data-dy-count]')
          ?? wrapper.querySelector('[data-dy-count-native]')
          ?? wrapper.querySelector('span');
        if (span) span.textContent = text;
      }
    }

    // Retire the pending state (no-op when the count was not rendered pending).
    for (const wrapper of wrappers) {
      wrapper.querySelector('[data-dy-count-skeleton]')?.remove();
      const native = /** @type {HTMLElement | null} */ (wrapper.querySelector('[data-dy-count-native]'));
      if (native) native.hidden = false;
    }

    for (const wrapper of wrappers) {
      if (wrapper instanceof HTMLElement) wrapper.style.visibility = 'visible';
    }
  }

  /**
   * Show or hide the no-results empty state based on DY's total. Only shown when DY returned a
   * definitive 0 (a filter/sort is active and nothing matched).
   *
   * @param {number | null} total
   * @returns {void}
   */
  #toggleNoResults(total) {
    const el = this.querySelector('[data-dy-no-results]');
    if (!(el instanceof HTMLElement)) return;
    el.hidden = total !== 0;
    if (total === 0) {
      const status = document.querySelector('[data-dy-status]');
      if (status) status.textContent = this.dataset.labelNoResults ?? '';
    }
  }

  /**
   * Set up an IntersectionObserver on the last visible grid card. When it enters the viewport,
   * append the next batch of DY skeletons; the MutationObserver then fills them at the new offset.
   * Only used once DY owns pagination (a filter/sort has been applied).
   *
   * @param {Element} grid
   * @param {number} pageSize
   * @returns {void}
   */
  #setupDyPagination(grid, pageSize) {
    this.#intersectObserver?.disconnect();
    this.#intersectObserver = null;

    if (!this.#dyTotal || this.#dyTotal <= this.#dyPage * pageSize) return;

    const items = grid.querySelectorAll('.product-grid__item:not([hidden])');
    const lastItem = /** @type {Element | undefined} */ (items[items.length - 1]);
    if (!lastItem) return;

    this.#intersectObserver = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        this.#intersectObserver?.disconnect();
        this.#intersectObserver = null;
        this.#dyPage += 1;
        this.#appendDySkeletons(grid, this.#dyPage, pageSize);
      },
      { rootMargin: '0px 0px 500px 0px' }
    );
    this.#intersectObserver.observe(lastItem);
  }

  /**
   * Create and append skeleton <li> elements for a new DY page. The MutationObserver fires
   * #collectAndFill which fills them from DY at the correct offset.
   *
   * @param {Element} grid
   * @param {number} page
   * @param {number} pageSize
   * @returns {void}
   */
  #appendDySkeletons(grid, page, pageSize) {
    const remaining = this.#dyTotal !== null
      ? Math.max(0, this.#dyTotal - (page - 1) * pageSize)
      : pageSize;
    const count = Math.min(pageSize, remaining);
    if (count <= 0) return;

    const fragment = document.createDocumentFragment();
    for (let i = 0; i < count; i++) fragment.appendChild(this.#buildSkeletonLi(page));
    // Insert before the CSS-priming card so it stays last in the grid.
    const primingCard = grid.querySelector('[data-css-prime]');
    if (primingCard) grid.insertBefore(fragment, primingCard);
    else grid.appendChild(fragment);
  }

  /**
   * Retire the pending pagination chrome once DY's first response has landed, whichever way it went.
   *
   * In DY mode the numbered nav is server-rendered `hidden` behind a shimmer, because the server's
   * page count is sized by Shopify collection membership and DY's set is usually smaller — painting
   * it would show a page count DY then corrects (see snippets/dy-pagination-skeleton.liquid). The
   * shimmer always goes here. The native nav is revealed only when DY is NOT going to render a pager
   * of its own: when it is, {@link #renderDyPagination} reveals the rebuilt nav at the end of this
   * fill, and revealing here would flash the native page count in between. When DY has no total to
   * size a pager with (timeout / feed gap) the grid is on native cards, so the native nav is the
   * correct one and is revealed unchanged.
   *
   * @param {Element} grid
   * @returns {void}
   */
  #settlePagination(grid) {
    const container = grid.parentElement;
    if (!container) return;

    container.querySelector(':scope > [data-dy-pagination-skeleton]')?.remove();

    const dyWillRenderPager =
      !this.#infiniteScroll && this.#dyTotal !== null && (this.#nativePagingDisabled || this.#isActive());
    if (dyWillRenderPager) return;

    const nav = /** @type {HTMLElement | null} */ (container.querySelector(':scope > nav.pagination'));
    if (nav) nav.hidden = false;
  }

  /**
   * Render (or refresh) numbered DY pagination controls below the grid, reflecting the current DY
   * page and total. Used instead of {@link #setupDyPagination} when the merchant is in paginated
   * (non-infinite-scroll) mode and DY owns pagination (a filter/sort is active). Reuses the SSR
   * `nav.pagination` when present so styling and DOM position match native paginated mode; the page
   * links are rebuilt as DY-driven links intercepted by {@link #handlePageClick}.
   *
   * @param {Element} grid
   * @param {number} pageSize
   * @returns {void}
   */
  #renderDyPagination(grid, pageSize) {
    const container = grid.parentElement;
    if (!container) return;

    // A null total means DY didn't answer (timed out / feed gap): the grid fell back to native cards,
    // whose SSR pagination is still valid. Leave whatever nav is in place rather than treating the
    // unknown total as a single page and stripping the pager — that would strand a keyboard/AT user
    // on page 1 with no controls. Only take over the nav once we have a real DY total to size it.
    if (this.#dyTotal === null) return;
    const totalPages = Math.ceil(this.#dyTotal / pageSize) || 1;
    let nav = /** @type {HTMLElement | null} */ (container.querySelector(':scope > nav.pagination'));

    // A single page needs no controls — drop any nav left over from the SSR paint or a prior query.
    if (totalPages <= 1) {
      // If focus is inside the nav we're about to remove, move it to the results region first so a
      // keyboard/AT user's focus is never silently dropped to <body>.
      if (nav?.contains(document.activeElement)) {
        document.getElementById('ResultsList')?.focus({ preventScroll: true });
      }
      nav?.remove();
      this.#pagerNav = null;
      return;
    }

    if (!nav) {
      nav = document.createElement('nav');
      nav.className = 'pagination';
      grid.after(nav);
    }
    // The SSR nav starts `hidden` in DY mode (see #settlePagination); now that it carries DY's own
    // page count rather than Shopify's, it is safe to show.
    nav.hidden = false;
    // Preserve keyboard focus across the innerHTML rebuild below (which discards the focused link):
    // remember the focused control's page target so its rebuilt equivalent can be re-focused.
    const focusedInNav = nav.contains(document.activeElement);
    const focusedPage = focusedInNav
      ? document.activeElement?.closest('[data-dy-page]')?.getAttribute('data-dy-page') ?? null
      : null;

    // Only set the landmark name when we actually have one — an empty aria-label suppresses the
    // accessible name rather than falling back to the element's implicit naming.
    if (this.dataset.labelPagination) nav.setAttribute('aria-label', this.dataset.labelPagination);
    // Underscore form matches the SSR pagination-controls.liquid convention (data-current_page).
    nav.setAttribute('data-current_page', String(this.#dyPage));
    nav.innerHTML = this.#buildPaginationHtml(this.#dyPage, totalPages);
    // The rebuilt markup reflects the settled page, so clear any transient busy state set while the
    // new page was fetching (see #goToDyPage).
    nav.removeAttribute('aria-busy');

    // Restore focus onto the rebuilt markup: the same page link if it still exists, else the results
    // region (e.g. the focused page became the current page and is now a non-focusable span).
    if (focusedInNav) {
      const restore = focusedPage ? nav.querySelector(`[data-dy-page="${focusedPage}"]`) : null;
      /** @type {HTMLElement | null} */ (restore ?? document.getElementById('ResultsList'))?.focus({ preventScroll: true });
    }

    // Bind once — the SSR nav's own `<a href>` links are replaced above with DY-driven links.
    if (!nav.dataset.dyBound) {
      nav.addEventListener('click', this.#handlePageClick);
      nav.dataset.dyBound = 'true';
    }
    this.#pagerNav = nav;
  }

  /**
   * Delegated click handler for the numbered DY pagination nav. Intercepts a page link and repaints
   * that DY page in place instead of navigating.
   *
   * @param {MouseEvent} e
   * @returns {void}
   */
  #handlePageClick = (e) => {
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || e.button !== 0) return;
    const target = /** @type {Element | null} */ (e.target instanceof Element ? e.target : null);
    const link = target?.closest('a[data-dy-page]');
    if (!link) return;
    e.preventDefault();
    const page = Number(link.getAttribute('data-dy-page'));
    if (Number.isFinite(page) && page >= 1) this.#goToDyPage(page);
  };

  /**
   * Repaint a specific DY page in numbered pagination mode: abort the current page's fills, rebuild
   * this page's skeletons (stamped with the target page so #fillPage queries the matching offset),
   * fill them from DY, and send the shopper to the top of the results — mirroring a native page nav.
   *
   * @param {number} page - 1-based target page.
   * @returns {void}
   */
  #goToDyPage(page) {
    const grid = this.#grid;
    if (!grid || page === this.#dyPage) return;

    const pageSize = Number(this.dataset.pageSize) || 24;
    this.#dyPage = page;

    // Reflect the DY page in the URL so a refresh restores it: the SSR renders that ?page=N and
    // #applyRequest honours it on the selection restore. replaceState (not push) so the browser back
    // button leaves the listing rather than stepping through every page view. page 1 drops the param
    // to match native paging (and the URL-sync convention in paginated-list.js).
    const url = new URL(window.location.href);
    if (page <= 1) url.searchParams.delete('page');
    else url.searchParams.set('page', String(page));
    history.replaceState(history.state, '', url.toString());

    // Cancel the previous page's in-flight fills before repainting.
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.#claimed = new WeakSet();
    this.#inFlight = 0;

    // Update the pager immediately so aria-current reflects the new page during the DY fetch window
    // (up to the timeout budget), then mark it busy until the fill settles and re-renders it. Without
    // this the nav would keep advertising the previous page as current for the whole fetch.
    this.#renderDyPagination(grid, pageSize);
    this.#pagerNav?.setAttribute('aria-busy', 'true');

    this.#resetSkeletons(grid, pageSize, page);
    this.#collectAndFill(grid);

    // Take the shopper to the top of the results for the new page, as a native page nav would.
    const region = document.getElementById('ResultsList');
    if (region) {
      region.scrollIntoView({ block: 'start' });
      region.focus({ preventScroll: true });
    }
  }

  /**
   * Build the inner `<ul>` markup for the numbered pagination nav, matching the class names in
   * snippets/pagination-controls.liquid so the shared pagination CSS styles it. Uses a compact,
   * fixed page window (see #paginationPages) shown identically on every viewport, with ellipsis
   * gaps — it does not use the SSR nav's `pagination__item--mobile-hide` desktop/mobile trimming,
   * since the window is already narrow enough (≤5 numbers) to fit the smallest viewport.
   *
   * @param {number} current - 1-based current page.
   * @param {number} total - Total page count (> 1).
   * @returns {string}
   */
  #buildPaginationHtml(current, total) {
    // Prefer the real theme chevron assets (passed as data-icon-*), so the DY nav matches the SSR
    // pagination icons exactly (same paths, rounded caps, --icon-stroke-width token). Fall back to a
    // minimal inline chevron only if the attributes are absent.
    const CHEVRON_LEFT =
      this.dataset.iconPrev ||
      '<svg viewBox="0 0 8 12" width="8" height="12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path d="M6.5 11 1.5 6l5-5" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    const CHEVRON_RIGHT =
      this.dataset.iconNext ||
      '<svg viewBox="0 0 8 12" width="8" height="12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path d="M1.5 1 6.5 6l-5 5" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    const prevLabel = this.#escapeAttr(this.dataset.labelPrev ?? '');
    const nextLabel = this.#escapeAttr(this.dataset.labelNext ?? '');
    const pageTemplate = this.dataset.labelPage ?? '__DY_PAGE__';
    const pageLabel = (/** @type {number} */ n) =>
      this.#escapeAttr(pageTemplate.replace('__DY_PAGE__', String(n)));

    let items = '';

    // Previous arrow
    if (current > 1) {
      items += `<li class="pagination__item"><a href="${this.#pageHref(current - 1)}" data-dy-page="${current - 1}" aria-label="${prevLabel}" class="pagination__link pagination__link--arrow">${CHEVRON_LEFT}</a></li>`;
    } else {
      items += `<li class="pagination__item"><span aria-label="${prevLabel}" class="pagination__link pagination__link--arrow pagination__link--disabled" aria-disabled="true">${CHEVRON_LEFT}</span></li>`;
    }

    // Page numbers: a compact window (first, last, current ± 1) with ellipsis gaps. The window is
    // narrow enough to show the same set on every viewport, so unlike the SSR nav there is no
    // mobile-hide trimming.
    for (const part of this.#paginationPages(current, total)) {
      if (part === 'gap') {
        items += '<li class="pagination__item pagination__item--gap"><span class="pagination__link pagination__link--gap" aria-hidden="true">&hellip;</span></li>';
        continue;
      }
      if (part === current) {
        items += `<li class="pagination__item"><span class="pagination__link pagination__link--page pagination__link--current" aria-current="page">${part}</span></li>`;
      } else {
        items += `<li class="pagination__item"><a href="${this.#pageHref(part)}" data-dy-page="${part}" aria-label="${pageLabel(part)}" class="pagination__link pagination__link--page">${part}</a></li>`;
      }
    }

    // Next arrow
    if (current < total) {
      items += `<li class="pagination__item"><a href="${this.#pageHref(current + 1)}" data-dy-page="${current + 1}" aria-label="${nextLabel}" class="pagination__link pagination__link--arrow">${CHEVRON_RIGHT}</a></li>`;
    } else {
      items += `<li class="pagination__item"><span aria-label="${nextLabel}" class="pagination__link pagination__link--arrow pagination__link--disabled" aria-disabled="true">${CHEVRON_RIGHT}</span></li>`;
    }

    return `<ul class="pagination__list" role="list">${items}</ul>`;
  }

  /**
   * Compute the compact page sequence to render: page 1, the last page, and current ± 1, with the
   * string 'gap' inserted wherever consecutive shown pages are more than one apart.
   *
   * @param {number} current
   * @param {number} total
   * @returns {Array<number | 'gap'>}
   */
  #paginationPages(current, total) {
    const shown = new Set([1, total]);
    for (let p = current - 1; p <= current + 1; p++) {
      if (p >= 1 && p <= total) shown.add(p);
    }
    const sorted = [...shown].sort((a, b) => a - b);
    /** @type {Array<number | 'gap'>} */
    const out = [];
    let prev = 0;
    for (const p of sorted) {
      if (prev > 0 && p - prev > 1) out.push('gap');
      out.push(p);
      prev = p;
    }
    return out;
  }

  /**
   * Build an href for a page link that preserves the current query and sets `page=n`. Clicks are
   * intercepted and handled by DY in place; the href is the progressive-enhancement / open-in-new-tab
   * target.
   *
   * This nav only exists while a DY filter/sort is active, so the current URL already carries the
   * `dyf_*`/`dysort` params (dy-search-facets mirrors them via replaceState). Opening the href in a
   * new tab restores the SAME filtered/sorted set AND lands on page n: the SSR renders that ?page=n
   * and #applyRequest honours it on the selection restore (#restorePending), so the page is
   * deep-linkable.
   *
   * @param {number} n
   * @returns {string}
   */
  #pageHref(n) {
    const url = new URL(window.location.href);
    // Mirror #goToDyPage's convention: page 1 drops the param (a bare URL is page 1) so the href
    // target matches the URL the in-app JS navigation writes for the same page.
    if (n <= 1) url.searchParams.delete('page');
    else url.searchParams.set('page', String(n));
    return this.#escapeAttr(url.pathname + url.search);
  }

  /**
   * Minimal attribute-context escape for values interpolated into the pagination HTML string.
   *
   * @param {string} value
   * @returns {string}
   */
  #escapeAttr(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  /**
   * Replace a placeholder's contents with a card lifted from a fetched ?view=card page. The card
   * comes from a separate document (DOMParser), so it is imported into this one; importing +
   * inserting upgrades the custom elements when their definitions are already loaded. The <li>'s
   * own pagination hooks (ref="cards[]", data-page) are preserved so infinite scroll keeps working.
   *
   * @param {HTMLLIElement} skeleton
   * @param {string} html - Full ?view=card page HTML for one product.
   * @param {string} handle - Shopify product handle; set as data-product-handle on the swapped tile.
   * @param {'dy' | 'native'} source - for the data-dy-source marker (debug/analytics).
   * @param {number} [index=0] - 0-based position within the fills array, used to set lazy loading.
   * @param {string | null} [slotId=null] - DY slot token for engagement reporting.
   * @param {string | null} [decisionId=null] - DY decision id for engagement reporting.
   * @returns {void}
   */
  #swap(skeleton, html, handle, source, index = 0, slotId = null, decisionId = null) {
    const doc = DyPlp.#parser.parseFromString(html, 'text/html');
    const section = doc.querySelector('#shopify-section-dy-product-card') ?? doc.body;
    const card = section?.querySelector('.product-card');
    if (!card) return;

    // The card's component CSS ({% stylesheet %} in card-gallery/price/etc.) is aggregated onto the
    // collection page by the hidden priming card the section renders in DY mode, so the swapped-in
    // card is styled without any client-side stylesheet injection here.
    skeleton.classList.remove('dy-skeleton');
    skeleton.removeAttribute('style');
    skeleton.dataset.dySource = source;
    skeleton.dataset.productHandle = handle;
    delete skeleton.dataset.dySkeleton;

    // Mirror the product id onto the tile so <disruptor-grid> counts it as a product card (its
    // slot maths keys off `.product-grid__item[data-product-id]`, matching the native grid).
    const productId = card.getAttribute('data-product-id');
    if (productId) skeleton.dataset.productId = productId;

    // The render seam page always emits cards as section index 0, so every hero image gets
    // loading="eager". Override for tiles below the fold to avoid a bandwidth burst on swap.
    if (index >= DyPlp.#EAGER_COUNT) {
      for (const img of card.querySelectorAll('img')) {
        img.loading = 'lazy';
      }
    }

    skeleton.replaceChildren(document.importNode(card, true));
    // Remove aria-hidden AFTER replaceChildren so AT never encounters the shimmer children
    // in the brief window between expose and content replacement.
    skeleton.removeAttribute('aria-hidden');

    if (source === 'dy' && slotId) {
      // Store ids on the <li> for the delegated grid click handler. Per-link
      // listeners don't survive the product-card CE's async inner re-render.
      skeleton.dataset.dySlotId = slotId;
      if (decisionId) skeleton.dataset.dyDecisionId = decisionId;
    }
  }

  /**
   * Delegated click handler on the product grid. Intercepts product link clicks
   * on DY-sourced cards to fire SLOT_CLICK engagement before navigating.
   *
   * Storing ids on the <li> (rather than per-link listeners) survives any inner
   * DOM re-renders by the product-card CE. Uses a keepalive simple request so it
   * survives the navigation — no artificial delay needed.
   *
   * @param {MouseEvent} e
   * @returns {void}
   */
  #handleSlotClick = (e) => {
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || e.button !== 0) return;

    // Skip clicks on buttons/forms (quick-add, swatches, etc.)
    if (/** @type {Element} */ (e.target)?.closest('button, [role="button"], form, input, label, select')) return;

    // Find the DY-tracked card first — works even when the click lands outside an <a>
    // (e.g. product-card-gallery__title-placeholder on no-image cards)
    const li = /** @type {Element} */ (e.target)?.closest('.product-grid__item[data-dy-slot-id]');
    if (!li) return;

    const slotId = /** @type {HTMLElement} */ (li).dataset.dySlotId;
    const decisionId = /** @type {HTMLElement} */ (li).dataset.dyDecisionId || null;
    if (!slotId) return;

    // If the click landed on a link that isn't a product URL, let it navigate normally.
    const targetLink = /** @type {Element} */ (e.target)?.closest('a');
    if (targetLink && !/** @type {HTMLAnchorElement} */ (targetLink).href?.includes('/products/')) return;

    const href = /** @type {HTMLAnchorElement} */ (targetLink)?.href
      || /** @type {HTMLAnchorElement} */ (li.querySelector('a[href*="/products/"]'))?.href;
    if (!href) return;

    e.preventDefault();

    // Fire-and-forget: reportSlotClick uses a keepalive fetch, so it survives
    // the navigation below — no artificial delay needed.
    this.#client?.reportSlotClick({ slotId }, decisionId);

    // Clear ids so a back-navigation re-click doesn't double-fire.
    delete /** @type {HTMLElement} */ (li).dataset.dySlotId;
    delete /** @type {HTMLElement} */ (li).dataset.dyDecisionId;

    window.location.href = href;
  };

  /**
   * Track outstanding page fills and reflect them as aria-busy on the grid.
   *
   * @param {Element} grid
   * @param {number} delta - +1 when a fill starts, -1 when it finishes.
   * @returns {void}
   */
  #setBusy(grid, delta) {
    const wasBusy = this.#inFlight > 0;
    this.#inFlight = Math.max(0, this.#inFlight + delta);
    const busy = this.#inFlight > 0;
    if (busy) grid.setAttribute('aria-busy', 'true');
    else grid.removeAttribute('aria-busy');
    // Only write to the live region on a state transition to avoid chatty announcements when
    // multiple pages are filling concurrently (each delta fires this method).
    if (busy !== wasBusy) {
      const status = document.querySelector('[data-dy-status]');
      if (status) {
        if (busy) {
          status.textContent = this.dataset.labelLoading ?? '';
        } else if (this.#dyTotal !== 0) {
          // Skip the "loaded" announcement when DY returned 0 results — #toggleNoResults has already
          // written the no-results message to this region and we must not overwrite it a frame later.
          status.textContent = this.dataset.labelLoaded ?? '';
          // Clear after announcement so the region is ready for the next fill cycle; identical
          // strings don't re-announce in aria-live without this reset.
          setTimeout(() => { if (status) status.textContent = ''; }, 500);
        }
      }
    }
  }
}

if (!customElements.get('dy-plp')) {
  customElements.define('dy-plp', DyPlp);
}
